import { Component, ViewEncapsulation, ChangeDetectionStrategy, signal, computed, inject, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LayoutService } from '../../../core/services/layout.service';

export interface Question {
  id: string;
  text: string;
  selected: boolean;
  isCustom?: boolean;
}

@Component({
  selector: 'app-schedule-promt',
  imports: [CommonModule, FormsModule],
  templateUrl: './schedule-promt.html',
  styleUrl: './schedule-promt.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:click)': 'closeAllMenus()' },
})
export class SchedulePromt implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly layoutService = inject(LayoutService);

  // -- Step navigation -------------------------------------------------------

  readonly currentStep = signal<number>(1);
  readonly totalSteps = 4;

  goToStep(step: number): void {
    if (step >= 1 && step <= this.totalSteps) this.currentStep.set(step);
  }

  nextStep(): void {
    this.goToStep(this.currentStep() + 1);
  }

  prevStep(): void {
    this.goToStep(this.currentStep() - 1);
  }

  cancelSchedule(): void {
    this.router.navigate(['/app/stories']);
  }

  // -- Step 1: Categories ----------------------------------------------------

  readonly selectedCategory = signal<number | null>(1);

  readonly categories = [
    { id: 1, name: 'Childhood',            icon: 'images/icons/memory-share-icons/fm-ms-icon1.svg', prompts: 4 },
    { id: 2, name: 'Family & Heritage',    icon: 'images/icons/memory-share-icons/fm-ms-icon2.svg', prompts: 4 },
    { id: 3, name: 'Love & Relationship',  icon: 'images/icons/memory-share-icons/fm-ms-icon3.svg', prompts: 4 },
    { id: 4, name: 'Career & Work',        icon: 'images/icons/memory-share-icons/fm-ms-icon4.svg', prompts: 4 },
    { id: 5, name: 'Major Life Moment',    icon: 'images/icons/memory-share-icons/fm-ms-icon5.svg', prompts: 4 },
    { id: 6, name: 'Challenge & Triumphs', icon: 'images/icons/memory-share-icons/fm-ms-icon6.svg', prompts: 4 },
    { id: 7, name: 'Wisdom & Advice',      icon: 'images/icons/memory-share-icons/fm-ms-icon7.svg', prompts: 4 },
    { id: 8, name: 'Fun Memories',          icon: 'images/icons/memory-share-icons/fm-ms-icon8.svg', prompts: 4 },
  ];

  readonly selectedCategoryName = computed(() => {
    const cat = this.categories.find(c => c.id === this.selectedCategory());
    return cat?.name ?? '';
  });

  selectCategory(id: number): void {
    this.selectedCategory.set(id);
  }

  // -- Step 2: Media type ----------------------------------------------------

  readonly mediaTypes = [
    { id: 'video',       label: 'Video Recording', description: 'They can record or upload video responses',              icon: 'images/icons/memory-share-icons/fm-ts-icon1.svg' },
    { id: 'audio',       label: 'Audio Recording', description: 'They can record or upload audio responses',              icon: 'images/icons/memory-share-icons/fm-ts-icon2.svg' },
    { id: 'image-audio', label: 'Image + Audio',   description: 'You provide a image, they answer with video or audio',   icon: 'images/icons/memory-share-icons/fm-ts-icon3.svg' },
  ];

  readonly selectedMedia = signal<string>('video');

  readonly selectedMediaLabel = computed(() => {
    const m = this.mediaTypes.find(t => t.id === this.selectedMedia());
    return m?.label ?? '';
  });

  selectMedia(id: string): void {
    this.selectedMedia.set(id);
  }

  // -- Step 3: Questions -----------------------------------------------------

  readonly questions = signal<Question[]>([
    { id: '1', text: 'What was your favorite childhood game?',                    selected: false, isCustom: false },
    { id: '2', text: 'Who was your best friend growing up?',                      selected: true,  isCustom: false },
    { id: '3', text: 'Share a memory about your grandparents',                    selected: false, isCustom: false },
    { id: '4', text: 'Tell me about your wedding day',                            selected: false, isCustom: false },
    { id: '5', text: 'Share a romantic memory that still makes you smile',        selected: false, isCustom: false },
  ]);

  readonly selectedQuestions = computed(() => this.questions().filter(q => q.selected));

  readonly openMenuId = signal<string | null>(null);
  readonly showCustomQuestion = signal(false);
  readonly customQuestionText = signal('');
  readonly editingQuestionId = signal<string | null>(null);
  readonly editingQuestionText = signal('');
  readonly deleteConfirmId = signal<string | null>(null);

  toggleQuestion(id: string): void {
    this.questions.update(list => list.map(q => q.id === id ? { ...q, selected: !q.selected } : q));
  }

  selectAllQuestions(): void {
    this.questions.update(list => list.map(q => ({ ...q, selected: true })));
  }

  clearAllQuestions(): void {
    this.questions.update(list => list.map(q => ({ ...q, selected: false })));
  }

  toggleQuestionMenu(id: string, event: Event): void {
    event.stopPropagation();
    this.openMenuId.update(current => current === id ? null : id);
  }

  closeAllMenus(): void {
    this.openMenuId.set(null);
  }

  toggleCustomQuestion(): void {
    this.showCustomQuestion.update(v => !v);
  }

  saveCustomQuestion(): void {
    const text = this.customQuestionText().trim();
    if (!text) return;
    const newId = Date.now().toString();
    this.questions.update(list => [
      ...list,
      { id: newId, text, selected: true, isCustom: true },
    ]);
    this.customQuestionText.set('');
    this.showCustomQuestion.set(false);
  }

  startEditQuestion(id: string): void {
    const q = this.questions().find(q => q.id === id);
    if (!q?.isCustom) return;
    this.editingQuestionId.set(id);
    this.editingQuestionText.set(q.text);
    this.openMenuId.set(null);
  }

  cancelEditQuestion(): void {
    this.editingQuestionId.set(null);
    this.editingQuestionText.set('');
  }

  saveEditQuestion(): void {
    const id = this.editingQuestionId();
    const text = this.editingQuestionText().trim();
    if (!id || !text) return;
    this.questions.update(list => list.map(q => q.id === id ? { ...q, text } : q));
    this.editingQuestionId.set(null);
    this.editingQuestionText.set('');
  }

  deleteQuestion(id: string): void {
    this.openMenuId.set(null);
    this.deleteConfirmId.set(id);
  }

  confirmDelete(): void {
    const id = this.deleteConfirmId();
    if (!id) return;
    this.deleteConfirmId.set(null);
    this.questions.update(list => list.filter(q => q.id !== id));
  }

  cancelDelete(): void {
    this.deleteConfirmId.set(null);
  }

  // -- Step 4: Send to -------------------------------------------------------

  readonly promptFrequency = signal('daily');
  readonly questionsPerPrompt = signal('1');
  readonly promptTitle = signal('');

  sendPrompt(): void {
    // TODO: API integration later
  }

  // -- Lifecycle -------------------------------------------------------------

  ngOnInit(): void {
    this.layoutService.setHideHeader(true);
  }

  ngOnDestroy(): void {
    this.layoutService.setHideHeader(false);
  }
}
